import { useSearchParams } from "next/navigation";
import { useRouter } from "nextjs-toploader/app";
import queryString from "query-string";
import { useCallback, useMemo } from "react";

import {
  filterSchema,
  FilterValues,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/filter-schema";
import { usePreserveFilters } from "@/stores/use-preserve-filters";

export default function useUrlState() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pushRoute = router.push;
  const { savePreservedFilters } = usePreserveFilters();

  const urlValues = useMemo((): FilterValues => {
    const parsedUrl = queryString.parse(searchParams.toString(), {
      arrayFormat: "bracket",
      parseBooleans: true,
    });

    let parsed = {};

    try {
      parsed = filterSchema.parse(parsedUrl);
    } catch (e) {
      console.error("Failed to parse URL", e);
    }

    return parsed;
  }, [searchParams]);

  const onUrlValuesChange = useCallback(
    (values: FilterValues) => {
      const viewMode = searchParams.get("viewMode");

      const paramsToSerialize: Record<string, unknown> = {
        ...values,
        ...(viewMode && { viewMode }),
      };

      const newSearch = queryString.stringify(paramsToSerialize, {
        skipNull: true,
        skipEmptyString: true,
        arrayFormat: "bracket",
      });

      const filtersToPreserve = queryString.stringify(values, {
        skipNull: true,
        skipEmptyString: true,
        arrayFormat: "bracket",
      });

      savePreservedFilters(filtersToPreserve);

      if (window.location.search !== `?${newSearch}`) {
        pushRoute(`?${newSearch}`);
      }
    },
    [pushRoute, searchParams, savePreservedFilters],
  );

  return { urlValues: urlValues, onUrlValuesChange: onUrlValuesChange };
}
